import { defineStore } from 'pinia'
import { JourneySearchResult } from '../types/journeyTypes'
import { Passenger, PassengerType } from '../types/passengerTypes'
import { Station } from '../types/stationTypes'
import { fetchDestinationSuggestions } from '../services/journeyService'
import { Booking, Ticket } from '../types/bookingTypes'
import { SelectedSeat } from '../types/seatTypes'
import { useMyTravelStore } from './myTravelStore'

export const useJourneyStore = defineStore('journey', {
  state: () => ({
    stationName: '' as string,
    uicStationCode: '' as string,
    OriginStation: { name: '', _u_i_c_station_code: '' } as Station,
    destinationStation: { name: '', _u_i_c_station_code: '' } as Station,
    destinationSuggestions: [] as string[],
    departureDate: (() => {
      const now = new Date()
      return now.toISOString().split('T')[0]
    })(),
    returnDate: '' as string,
    showOutbound: true,
    passengerType: null as PassengerType | null,
    journeySearchResults: null as JourneySearchResult | null,
    allStations: [] as Station[],
    bookingResponse: null as string | null,
    bookingData: null as Booking | null,
    passengers: [] as Passenger[],
    selectedSeats: [] as SelectedSeat[],
    selectedProducts: [] as Ticket[],
    selectedBundle: null as any | null,
    isRebooking: false,
    rebookingType: '' as 'full' | 'outbound' | 'inbound' | '',
    originalBookingNumber: '' as string,
    rebookPayload: null as any,
    isInitialized: false,
    requestedProductTypes: [] as string[],
    missingProductTypes: [] as string[],
    missingProductTypesPerJourney: {} as Record<string, string[]>,
    isTransferJourney: false as boolean,
  }),
  actions: {
    async setOriginStation(station: Station) {
      this.OriginStation = station
      this.destinationSuggestions = [] // Reset to empty array before fetching
      try {
        const suggestions = await fetchDestinationSuggestions(
          station._u_i_c_station_code
        )
        this.destinationSuggestions = suggestions
      } catch (error) {
        console.error('Error fetching destination suggestions:', error)
      }
    },
    setDestinationStation(station: Station) {
      this.destinationStation = station
    },
    setDepartureDate(date: string) {
      this.departureDate = date
    },
    setReturnDate(date: string | null) {
      this.returnDate = date ?? ''
      
      if (date) {
        sessionStorage.setItem('rebooking_return_date', date)
      } else {
        sessionStorage.removeItem('rebooking_return_date')
      }
    },
    //deprecated
    setPassengerType(type: PassengerType) {
      this.passengerType = type
    },
    setPassengers(passengers: Passenger[]) {
      this.passengers = passengers
    },
    setJourneySearchResults(results: JourneySearchResult) {
      this.journeySearchResults = results
      this.selectedProducts = []
      this.selectedSeats = []
      this.selectedBundle = null

      const myTravelStore = useMyTravelStore()
      myTravelStore.reset()
    },
    setAllStations(stations: Station[]) {
      this.allStations = stations
    },
    setBookingResponse(response: string) {
      this.bookingResponse = response
    },
    setBookingData(data: Booking) {
      this.bookingData = data
    },
    setSelectedBundle(bundle: any) {
      this.selectedBundle = bundle
    },
    addSelectedSeat(seat: SelectedSeat) {
      this.selectedSeats = this.selectedSeats.filter(
        (s) => s.seat_number !== seat.seat_number
      )
      this.selectedSeats.push(seat)
    },
    removeSelectedSeat(seat_number: string) {
      this.selectedSeats = this.selectedSeats.filter(
        (seat) => seat.seat_number !== seat_number
      )
    },
    removeSelectedProduct(tariffCode: string) {
      this.selectedProducts = this.selectedProducts.filter(
        (product) => product.product_family !== tariffCode
      )
    },
    addTicket(ticket: any) {
      const currentProducts = [...this.selectedProducts]

      const filteredProducts = currentProducts.filter(
        (p) =>
          !(
            p.passenger_id === ticket.passenger_id &&
            p.travelIndex === ticket.travelIndex
          )
      )
      this.selectedProducts = [...filteredProducts, ticket]
    },
    resetStore() {
      this.$reset()
    },
    initializeRebooking(
      type: 'full' | 'outbound' | 'inbound',
      bookingNumber: string
    ) {
      this.isRebooking = true
      this.rebookingType = type
      this.originalBookingNumber = bookingNumber
    },

    setFromStation(stationId: string) {
      const station = this.allStations.find(
        (s) => s._u_i_c_station_code === stationId
      )
      if (station) {
        this.setOriginStation(station)
      }
    },

    setToStation(stationId: string) {
      const station = this.allStations.find(
        (s) => s._u_i_c_station_code === stationId
      )
      if (station) {
        this.setDestinationStation(station)
      }
    },

    prefillRebookingData(booking: Booking) {
      if (this.rebookingType === 'outbound' || this.rebookingType === 'full') {
        const outboundSegment = booking.outbound_booking_tariff_segments?.[0]
        if (outboundSegment) {
          this.setFromStation(
            outboundSegment.departure_station._u_i_c_station_code
          )
          this.setToStation(outboundSegment.arrival_station._u_i_c_station_code)

          if (this.rebookingType === 'outbound') {
            this.setDepartureDate(outboundSegment.travel_date)
          }
        }
      }

      if (this.rebookingType === 'inbound' || this.rebookingType === 'full') {
        const inboundSegment = booking.inbound_booking_tariff_segments?.[0]
        if (inboundSegment) {
          if (this.rebookingType === 'full') {
            this.setReturnDate(inboundSegment.travel_date)
          } else {
            this.setFromStation(
              inboundSegment.departure_station._u_i_c_station_code
            )
            this.setToStation(
              inboundSegment.arrival_station._u_i_c_station_code
            )
            this.setDepartureDate(inboundSegment.travel_date)
          }
        }
      }

      this.passengers = booking.passengers.map((passenger) => ({
        id: passenger.id,
        type: passenger.type,
        first_name: passenger.first_name || '',
        last_name: passenger.last_name || '',
        email: passenger.email || '',
        phone_number: passenger.phone_number || '',
        discount_cards: passenger.discount_cards || [],
        protect_privacy: passenger.protect_privacy || false,
      }))

      this.selectedSeats = []
      this.selectedProducts = []
    },

    prepareRebookingQuery(
      booking: Booking,
      type: 'full' | 'outbound' | 'inbound'
    ) {
      if (!booking.booking_number) {
        throw new Error('Booking number is required')
      }

      let query: Record<string, string> = {
        isRebooking: 'true',
        bookingNumber: booking.booking_number,
        type,
      }

      if (type === 'full' || type === 'outbound') {
        const outboundSegment = booking.outbound_booking_tariff_segments?.[0]
        if (!outboundSegment) {
          throw new Error('No outbound segment found')
        }
        query = {
          ...query,
          from: outboundSegment.departure_station._u_i_c_station_code,
          to: outboundSegment.arrival_station._u_i_c_station_code,
        }
      } else {
        const inboundSegment = booking.inbound_booking_tariff_segments?.[0]
        if (!inboundSegment) {
          throw new Error('No inbound segment found')
        }
        query = {
          ...query,
          from: inboundSegment.departure_station._u_i_c_station_code,
          to: inboundSegment.arrival_station._u_i_c_station_code,
        }
      }

      return query
    },
    setShowOutbound(value: boolean) {
      this.showOutbound = value
    },
    clearDiscountCards() {
      this.passengers = this.passengers.map((passenger) => ({
        ...passenger,
        discount_cards: [],
      }))
    },
    setRebookPayload(payload: any) {
      this.rebookPayload = payload
    },
    setInitialized(value: boolean) {
      this.isInitialized = value;
    },
    
    setIsTransferJourney(value: boolean) {
      this.isTransferJourney = value
    },
    
    setRequestedProductTypes(types: string[]) {
      this.requestedProductTypes = [...new Set(types)];
    },
    
    checkMissingProductTypes() {
      if (!this.journeySearchResults || !this.requestedProductTypes.length) {
        this.missingProductTypes = [];
        return;
      }

      const offerData = this.journeySearchResults.data?.offer || this.journeySearchResults.offer;
      if (!offerData) {
        this.missingProductTypes = [...this.requestedProductTypes];
        return;
      }
      
      try {
        const travels = offerData.travels || [];
        if (travels.length === 0) {
          this.missingProductTypes = [...this.requestedProductTypes];
          return;
        }

        const availableProductTypesAcrossAllJourneys = new Set<string>();      
        const missingProductTypesPerJourney: Record<string, string[]> = {};
        
        travels.forEach(travel => {
          if (!travel.routes) return;
          
          travel.routes.forEach(route => {
            if (!route.bundles) return;
            
            const journeyAvailableProductTypes = new Set<string>();
            
            route.bundles.forEach(bundle => {
              if (!bundle.items) return;
              
              bundle.items.forEach(item => {
                if (item.product_type) {
                  journeyAvailableProductTypes.add(item.product_type);
                  availableProductTypesAcrossAllJourneys.add(item.product_type);
                }
              });
            });
            
            const journeyMissingProductTypes = this.requestedProductTypes.filter(
              type => !journeyAvailableProductTypes.has(type)
            );
            
            if (journeyMissingProductTypes.length > 0) {
              missingProductTypesPerJourney[route.id] = journeyMissingProductTypes;
            }
          });
        });

        const allMissingProductTypes = new Set<string>();
        Object.values(missingProductTypesPerJourney).forEach(missingTypes => {
          missingTypes.forEach(type => allMissingProductTypes.add(type));
        });
        
        this.missingProductTypesPerJourney = missingProductTypesPerJourney;
        this.missingProductTypes = Array.from(allMissingProductTypes);

      } catch (error) {
        console.error('Error processing journey search results:', error);
        this.missingProductTypes = [...this.requestedProductTypes];
      }
    },
  },
  getters: {
    mappedDestinationSuggestions: (state) => {
      const suggestions = state.destinationSuggestions || []
      return suggestions.map((uicCode) => {
        const matchedStation = state.allStations.find(
          (station: Station) => station._u_i_c_station_code === uicCode
        )
        return {
          uicCode,
          name: matchedStation ? matchedStation.name : uicCode,
        }
      })
    },
  },
})
